import { useState, useEffect, useRef, Fragment, useMemo } from "react";
import CommonAPI from '~/api/commonApi';
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCog, faSort, faSortUp, faSortDown, faChevronRight, faChevronDown, faGripVertical } from "@fortawesome/free-solid-svg-icons";
import classNames from "classnames/bind";
import styles from "./Table.module.scss";
import SkeletonTableLoading from "../SkeletonTableLoading";

const cx = classNames.bind(styles);

function Table({ tableConfig }) {
    const {
        columns = [],
        data = [],
        tableKey = "Empty",
        hasShowAction = true,
        hasShowHideColumn = true,
        leftInlineButtons = [],
        topLeftButtons = [],
        extraButtons = [],
        inlineButtons = [],
        sortColumn = "",
        sortDirection = "",
        onSort = null
    } = tableConfig;

    const [expandedCells, setExpandedCells] = useState({});
    const [visibleColumns, setVisibleColumns] = useState([]);
    const [tempColumns, setTempColumns] = useState([]);
    const [showAction, setShowAction] = useState(false);
    const [columnOrder, setColumnOrder] = useState([]);
    const [tempColumnOrder, setTempColumnOrder] = useState([]);
    const [draggedMenuKey, setDraggedMenuKey] = useState(null);
    const [dragOverMenuKey, setDragOverMenuKey] = useState(null);
    const [draggedHeaderKey, setDraggedHeaderKey] = useState(null);
    const [dragOverHeaderKey, setDragOverHeaderKey] = useState(null);


    const actuallyHasAction = useMemo(() => {
        return columns.some(c => c.key && c.key.includes("Action"));
    }, [columns]);

    /* =========================================================
       ORDERED COLUMNS
       - SelectCheckbox (nếu có) luôn đứng đầu
       - RowNumber (nếu có) đứng thứ 2
       - Action (key === "Action") là 1 group
    ========================================================= */
    const orderedColumns = useMemo(() => {
        if (!columns.length) return [];

        const checkboxCol = columns.find(c => c.key === "SelectCheckbox");
        const rowNumberCol = columns.find(c => c.key === "RowNumber");

        const actionCols = [];
        let otherCols = [];

        const colMap = {};
        for (let i = 0; i < columns.length; i++) {
            const col = columns[i];
            if (col.key === "SelectCheckbox" || col.key === "RowNumber") continue;

            if (col.key && col.key.includes("Action")) {
                actionCols.push(col);
            } else {
                colMap[col.key] = col;
            }
        }

        if (columnOrder && columnOrder.length > 0) {
            columnOrder.forEach(key => {
                if (colMap[key]) {
                    otherCols.push(colMap[key]);
                    delete colMap[key];
                }
            });
        }
        
        Object.values(colMap).forEach(col => {
            otherCols.push(col);
        });

        const prefixCols = [];
        if (checkboxCol) prefixCols.push(checkboxCol);
        if (rowNumberCol) prefixCols.push(rowNumberCol);

        return showAction
            ? [...prefixCols, ...actionCols, ...otherCols]
            : [...prefixCols, ...otherCols, ...actionCols];
    }, [columns, showAction, columnOrder]);

    /* =========================================================
       CONFIG UI: render all columns
    ========================================================= */
    const renderableColumnsForConfig = useMemo(() => {
        const colMap = {};
        columns.forEach(c => { colMap[c.key] = c; });
        return tempColumnOrder.map(key => colMap[key]).filter(Boolean);
    }, [columns, tempColumnOrder]);

    /* =========================================================
       LOAD COLUMN SETTING
    ========================================================= */
    const baseOtherCols = useMemo(() => {
        return columns.filter(c => !(c.key && c.key.includes("Action")) && c.key !== "SelectCheckbox" && c.key !== "RowNumber");
    }, [columns]);

    useEffect(() => {
        CommonAPI.getColumnSetting(tableKey)
            .then(res => {
                let cols = [];
                let order = [];

                if (res.data?.VisibleColumns) {
                    let parsedCols = JSON.parse(res.data.VisibleColumns || "[]");
                    if (parsedCols.includes("Action")) {
                        parsedCols = parsedCols.filter(k => k !== "Action");
                        const currentActionKeys = columns.filter(c => c.key && c.key.includes("Action")).map(c => c.key);
                        currentActionKeys.forEach(k => {
                            if (!parsedCols.includes(k)) parsedCols.push(k);
                        });
                    }
                    
                    if (!parsedCols.includes("SelectCheckbox") && columns.some(c => c.key === "SelectCheckbox")) parsedCols.push("SelectCheckbox");
                    if (!parsedCols.includes("RowNumber") && columns.some(c => c.key === "RowNumber")) parsedCols.push("RowNumber");
                    if (!parsedCols.includes("Action")) parsedCols.push("Action");
                    
                    cols = parsedCols;

                    const visibleOrder = parsedCols.filter(k => k !== "Action" && k !== "SelectCheckbox" && k !== "RowNumber");
                    const hiddenOrder = baseOtherCols.map(c => c.key).filter(k => !visibleOrder.includes(k));
                    order = [...visibleOrder, ...hiddenOrder];
                } else {
                    cols = columns.map(c => c.key);
                    order = baseOtherCols.map(c => c.key);
                }

                setVisibleColumns(cols);
                setTempColumns(cols);
                setColumnOrder(order);
                setTempColumnOrder(order);
                setShowAction(!!res.data?.IsShowAction);
            })
            .catch(() => {});
    }, [tableKey, columns, baseOtherCols]);

    /* =========================================================
       TOGGLE TEMP COLUMN (GROUP BASED)
    ========================================================= */
    const toggleTempColumn = (key) => {
        setTempColumns(prev =>
            prev.includes(key)
                ? prev.filter(c => c !== key)
                : [...prev, key]
        );
    };

    const visibleColumnsRef = useRef([]);
    useEffect(() => {
        visibleColumnsRef.current = visibleColumns;
    }, [visibleColumns]);

    /* =========================================================
       SAVE COLUMN CONFIG
    ========================================================= */
    const handleSaveChangesColumnConfig = () => {
        const sortedVisibleColumns = tempColumnOrder.filter(k => tempColumns.includes(k));
        
        const finalVisible = [...sortedVisibleColumns, "Action", "SelectCheckbox", "RowNumber"].filter(Boolean);

        const payload = {
            TableKey: tableKey,
            VisibleColumns: JSON.stringify(finalVisible),
            IsShowAction: showAction,
            Action: "ColumnSetting"
        };

        CommonAPI.insUpdColumnSetting(payload)
            .then(() => {
                setVisibleColumns([...tempColumns]);
                visibleColumnsRef.current = [...tempColumns];
                setColumnOrder([...tempColumnOrder]);
            })
            .catch(() => {})
            .finally(() => {
                const el = document.getElementById("dropdownColumnConfig");
                const instance = window.bootstrap.Dropdown.getInstance(el);
                instance?.hide();
            });
    };

    /* =========================================================
       DRAG AND DROP HANDLERS (CONFIG MENU)
    ========================================================= */
    const handleDragStartMenu = (e, key) => {
        e.dataTransfer.effectAllowed = 'move';
        e.dataTransfer.setData("menuKey", key);
        setDraggedMenuKey(key);
    };

    const handleDragOverMenu = (e, key) => {
        e.preventDefault();
        e.dataTransfer.dropEffect = 'move';
        setDragOverMenuKey(key);
    };

    const handleDropMenu = (e, dropKey) => {
        e.preventDefault();
        const dragKey = e.dataTransfer.getData("menuKey");
        if (dragKey && dragKey !== dropKey) {
            setTempColumnOrder(prev => {
                const newOrder = [...prev];
                const dragIndex = newOrder.indexOf(dragKey);
                const dropIndex = newOrder.indexOf(dropKey);
                if (dragIndex > -1 && dropIndex > -1) {
                    newOrder.splice(dragIndex, 1);
                    newOrder.splice(dropIndex, 0, dragKey);
                }
                return newOrder;
            });
        }
        setDraggedMenuKey(null);
        setDragOverMenuKey(null);
    };

    /* =========================================================
       DRAG AND DROP HANDLERS (TABLE HEADERS)
    ========================================================= */
    const handleDragStartHeader = (e, key) => {
        e.dataTransfer.effectAllowed = 'move';
        e.dataTransfer.setData("headerKey", key);
        setDraggedHeaderKey(key);
    };

    const handleDragOverHeader = (e, key) => {
        e.preventDefault();
        e.dataTransfer.dropEffect = 'move';
        setDragOverHeaderKey(key);
    };

    const handleDropHeader = (e, dropKey) => {
        e.preventDefault();
        const dragKey = e.dataTransfer.getData("headerKey");
        if (dragKey && dragKey !== dropKey) {
            const newOrder = [...columnOrder];
            const dragIndex = newOrder.indexOf(dragKey);
            const dropIndex = newOrder.indexOf(dropKey);
            if (dragIndex > -1 && dropIndex > -1) {
                newOrder.splice(dragIndex, 1);
                newOrder.splice(dropIndex, 0, dragKey);
                setColumnOrder(newOrder);
                setTempColumnOrder(newOrder);

                const sortedVisibleColumns = newOrder.filter(k => visibleColumns.includes(k));
                
                const finalVisible = [...sortedVisibleColumns, "Action", "SelectCheckbox", "RowNumber"].filter(Boolean);

                CommonAPI.insUpdColumnSetting({
                    TableKey: tableKey,
                    VisibleColumns: JSON.stringify(finalVisible),
                    IsShowAction: showAction,
                    Action: "ColumnSetting"
                }).catch(() => {});
            }
        }
        setDraggedHeaderKey(null);
        setDragOverHeaderKey(null);
    };

    /* =========================================================
       TOGGLE SHOW ACTION LEFT / RIGHT
    ========================================================= */
    const toggleShowAction = (checked) => {
        setShowAction(checked);

        CommonAPI.insUpdColumnSetting({
            TableKey: tableKey,
            IsShowAction: checked,
            VisibleColumns: '[]',
            Action: "ShowAction",
        }).catch(() => {});
    };

    /* =========================================================
       MORE / LESS CELL
    ========================================================= */
    const toggleCell = (rowIndex, colKey) => {
        const cellId = `${rowIndex}-${colKey}`;
        setExpandedCells(prev => ({
            ...prev,
            [cellId]: !prev[cellId]
        }));
    };

    /* =========================================================
       RESET TEMP WHEN DROPDOWN CLOSE
    ========================================================= */
    useEffect(() => {
        const el = document.getElementById("dropdownColumnConfig");
        if (!el) return;

        const handler = () => {
            setTempColumns(visibleColumnsRef.current);
        };

        el.addEventListener("hidden.bs.dropdown", handler);
        return () => el.removeEventListener("hidden.bs.dropdown", handler);
    }, []);

    /* =========================================================
       CALCULATE ROW SPANS FOR MERGEBY COLUMNS
    ========================================================= */
    const spans = useMemo(() => {
        const spanMap = {};
        orderedColumns.forEach(col => {
            if (col.mergeBy) {
                let currentVal = null;
                let startRow = 0;
                data.forEach((row, rIndex) => {
                    const val = row[col.mergeBy];
                    if (rIndex === 0) {
                        currentVal = val;
                        spanMap[`${rIndex}-${col.key}`] = 1;
                        startRow = 0;
                    } else if (val === currentVal) {
                        spanMap[`${startRow}-${col.key}`] += 1;
                        spanMap[`${rIndex}-${col.key}`] = 0; // 0 means do not render
                    } else {
                        currentVal = val;
                        startRow = rIndex;
                        spanMap[`${rIndex}-${col.key}`] = 1;
                    }
                });
            }
        });
        return spanMap;
    }, [data, orderedColumns]);

    /* =========================================================
       GROUPED DATA LOGIC
    ========================================================= */
    const [expandedRowGroups, setExpandedRowGroups] = useState({});

    const toggleRowGroup = (groupId) => {
        setExpandedRowGroups(prev => ({
            ...prev,
            [groupId]: !prev[groupId]
        }));
    };

    const groupedDataList = useMemo(() => {
        if (!tableConfig.groupBy) return null;
        const groupsMap = new Map();
        data.forEach(row => {
            const val = row[tableConfig.groupBy.key];
            if (!groupsMap.has(val)) {
                groupsMap.set(val, []);
            }
            groupsMap.get(val).push(row);
        });
        return Array.from(groupsMap.entries()).map(([key, rows]) => ({ key, rows }));
    }, [data, tableConfig.groupBy]);

    /* =========================================================
       CUSTOM SCROLLBAR & STICKY HEADER
    ========================================================= */
    const tableWrapRef = useRef(null);
    const tableRef = useRef(null);
    const customScrollbarRef = useRef(null);
    const [tableWidth, setTableWidth] = useState(0);

    useEffect(() => {
        if (!tableRef.current) return;
        const updateWidth = () => {
            if (tableRef.current) {
                setTableWidth(tableRef.current.scrollWidth);
            }
        };
        updateWidth();
        const observer = new ResizeObserver(() => updateWidth());
        observer.observe(tableRef.current);
        return () => observer.disconnect();
    }, [data, visibleColumns, orderedColumns, expandedRowGroups]);

    // Sticky header logic — position: fixed for absolute zero vertical jitter
    const isStickyRef = useRef(false);

    useEffect(() => {
        const onScroll = () => {
            if (!tableWrapRef.current || !tableRef.current) return;
            const rect = tableWrapRef.current.getBoundingClientRect();
            // Find the real thead, not our placeholder
            const theads = tableRef.current.querySelectorAll('thead');
            const thead = Array.from(theads).find(el => !el.classList.contains('sticky-placeholder'));
            if (!thead) return;

            const stickyOffset = 65; // navbar height
            const shouldStick = rect.top < stickyOffset && rect.bottom > stickyOffset + 50;

            if (shouldStick) {
                if (!isStickyRef.current) {
                    isStickyRef.current = true;
                    
                    const ths = thead.querySelectorAll('th');
                    // Capture computed widths before taking out of flow
                    const widths = Array.from(ths).map(th => th.getBoundingClientRect().width);
                    const theadHeight = thead.getBoundingClientRect().height;

                    // Create placeholder to prevent tbody jump
                    const placeholder = document.createElement('thead');
                    placeholder.className = 'sticky-placeholder';
                    placeholder.style.height = `${theadHeight}px`;
                    placeholder.style.display = 'table-header-group';
                    placeholder.style.visibility = 'hidden';
                    const tr = document.createElement('tr');
                    widths.forEach(w => {
                        const ph = document.createElement('th');
                        ph.style.width = `${w}px`;
                        ph.style.minWidth = `${w}px`;
                        ph.style.padding = '0';
                        ph.style.border = 'none';
                        tr.appendChild(ph);
                    });
                    placeholder.appendChild(tr);
                    thead.parentNode.insertBefore(placeholder, thead.nextSibling);

                    // Make original fixed
                    thead.style.position = 'fixed';
                    thead.style.top = `${stickyOffset}px`;
                    thead.style.zIndex = '100';
                    thead.style.width = `${tableRef.current.getBoundingClientRect().width}px`;
                    thead.style.transition = 'none';

                    // Lock widths on original ths
                    ths.forEach((th, i) => {
                        th.dataset.origMinWidth = th.style.minWidth || '';
                        th.style.width = `${widths[i]}px`;
                        th.style.minWidth = `${widths[i]}px`;
                        th.style.maxWidth = `${widths[i]}px`;
                        th.style.boxSizing = 'border-box';
                    });
                }

                // Continuously sync horizontal position and clip boundaries
                const scrollLeft = tableWrapRef.current.scrollLeft;
                thead.style.left = `${rect.left - scrollLeft}px`;
                
                // Calculate clip-path so the fixed header does not overflow the tableWrap's visual boundaries on the screen
                const tableWidth = tableRef.current.getBoundingClientRect().width;
                const leftClip = Math.max(0, scrollLeft);
                const rightClip = Math.max(0, tableWidth - scrollLeft - rect.width);
                thead.style.clipPath = `inset(-10px ${rightClip}px -10px ${leftClip}px)`;

            } else if (isStickyRef.current) {
                isStickyRef.current = false;
                
                // Restore original styles
                thead.style.position = '';
                thead.style.top = '';
                thead.style.left = '';
                thead.style.zIndex = '';
                thead.style.width = '';
                thead.style.clipPath = '';
                
                // Restore th widths
                const ths = thead.querySelectorAll('th');
                ths.forEach(th => {
                    th.style.width = '';
                    th.style.minWidth = th.dataset.origMinWidth || '';
                    th.style.maxWidth = '';
                    th.style.boxSizing = '';
                });

                // Remove placeholder
                const placeholder = tableRef.current.querySelector('.sticky-placeholder');
                if (placeholder) placeholder.remove();
            }
        };

        window.addEventListener('scroll', onScroll, { passive: true });
        window.addEventListener('resize', onScroll, { passive: true });
        
        // Ensure horizontal scrolling of the container also updates the fixed header position
        if (tableWrapRef.current) {
            tableWrapRef.current.addEventListener('scroll', onScroll, { passive: true });
        }
        
        const scrollParent = tableWrapRef.current?.closest('.content') || tableWrapRef.current?.closest('.simplebar-content-wrapper');
        if (scrollParent) {
            scrollParent.addEventListener('scroll', onScroll, { passive: true });
        }
        return () => {
            window.removeEventListener('scroll', onScroll);
            window.removeEventListener('resize', onScroll);
            if (tableWrapRef.current) tableWrapRef.current.removeEventListener('scroll', onScroll);
            if (scrollParent) scrollParent.removeEventListener('scroll', onScroll);
        };
    }, []);    const handleTableScroll = (e) => {
        if (customScrollbarRef.current && customScrollbarRef.current.scrollLeft !== e.target.scrollLeft) {
            customScrollbarRef.current.scrollLeft = e.target.scrollLeft;
        }
    };

    const handleCustomScrollbarScroll = (e) => {
        if (tableWrapRef.current && tableWrapRef.current.scrollLeft !== e.target.scrollLeft) {
            tableWrapRef.current.scrollLeft = e.target.scrollLeft;
        }
    };

    /* =========================================================
       RENDER
    ========================================================= */
    return (
        <div className={`${cx("tableShell")} global-table-shell`}>
            {/* TABLE CONTROLS TOOLBAR */}
            {((hasShowAction && actuallyHasAction) || hasShowHideColumn || extraButtons.length > 0 || leftInlineButtons.length > 0 || topLeftButtons.length > 0) && (
                <div className="d-flex flex-column align-items-start justify-content-start gap-3 px-4 mb-3 rounded-3 py-3" style={{ 
                    backgroundColor: '#fff', 
                    border: '1px solid #e2e8f0'
                }}>
                    {/* TOP LEFT BUTTONS */}
                    {topLeftButtons.length > 0 && (
                        <div className="d-flex align-items-center gap-2 w-100">
                            {topLeftButtons.map((btn, idx) => (
                                <Fragment key={idx}>{btn}</Fragment>
                            ))}
                        </div>
                    )}

                    <div className="d-flex align-items-center justify-content-between w-100 flex-wrap gap-4">
                    {/* LEFT SIDE INLINE BUTTONS */}
                    <div className="d-flex align-items-center gap-2">
                        {leftInlineButtons.length > 0 && leftInlineButtons.map((btn, idx) => (
                            <Fragment key={idx}>{btn}</Fragment>
                        ))}
                    </div>

                    <div className="d-flex align-items-center gap-4 flex-wrap">
                    {/* SHOW ACTION LEFT */}
                    {hasShowAction && actuallyHasAction && (
                        <div className="form-check mb-0 d-flex align-items-center ps-0">
                            <input
                                type="checkbox"
                                className="form-check-input cursor-pointer m-0 me-2"
                                id="chk-show-action"
                                checked={showAction}
                                onChange={(e) => toggleShowAction(e.target.checked)}
                            />
                            <label className="form-check-label fs-9 fw-bold text-body-emphasis cursor-pointer" htmlFor="chk-show-action" style={{ paddingTop: '2px' }}>
                                Show action on left side
                            </label>
                        </div>
                    )}

                    {/* SHOW / HIDE COLUMN CONFIG */}
                    {hasShowHideColumn && (
                        <div className="dropdown">
                            <a
                                href="#!"
                                data-bs-toggle="dropdown"
                                role="button"
                                id="dropdownColumnConfig"
                                data-bs-auto-close="outside"
                                className="text-primary d-flex align-items-center fw-bold text-decoration-none fs-9"
                                style={{ transition: 'color 0.2s' }}
                            >
                                <FontAwesomeIcon icon={faCog} className="me-2" />
                                Show/Hide Column Config
                            </a>

                            <div
                                className={cx("configMenu", "dropdown-menu py-0 border border-1 border-secondary-subtle rounded-4 mt-2")}
                                onClick={(e) => e.stopPropagation()}
                                style={{ minWidth: '280px', overflow: 'hidden', boxShadow: '0 10px 40px rgba(0, 0, 0, 0.15)' }}
                            >
                                <div className="card border-0">
                                    <div className="card-header bg-light border-bottom py-3 px-4">
                                        <h6 className="mb-0 text-body-emphasis fw-bold fs-9">Customize Columns</h6>
                                    </div>
                                    <div className="card-body p-0" style={{ maxHeight: '350px', overflowY: 'auto' }}>
                                        <div className="list-group list-group-flush">
                                            {renderableColumnsForConfig.map((col, i) => (
                                                <div 
                                                    key={i} 
                                                    className={`list-group-item border-0 py-2 px-4 d-flex align-items-center justify-content-between transition-base cursor-pointer ${draggedMenuKey === col.key ? 'opacity-50' : ''} ${dragOverMenuKey === col.key ? 'border-top border-primary border-2 bg-light' : 'hover-bg-light'}`}
                                                    onClick={() => toggleTempColumn(col.key)}
                                                    draggable
                                                    onDragStart={(e) => handleDragStartMenu(e, col.key)}
                                                    onDragOver={(e) => handleDragOverMenu(e, col.key)}
                                                    onDrop={(e) => handleDropMenu(e, col.key)}
                                                    onDragEnd={() => { setDraggedMenuKey(null); setDragOverMenuKey(null); }}
                                                >
                                                    <div className="text-muted me-3" style={{ cursor: 'grab' }} onClick={(e) => e.stopPropagation()}>
                                                        <FontAwesomeIcon icon={faGripVertical} />
                                                    </div>
                                                    <label
                                                        className="form-check-label fs-9 fw-semibold text-body-secondary mb-0 cursor-pointer"
                                                        style={{ flex: 1, pointerEvents: 'none' }}
                                                    >
                                                        {col.key === "Action"
                                                            ? "Action"
                                                            : col.name
                                                                ?.toLowerCase()
                                                                .replace(/\b\w/g, c => c.toUpperCase())
                                                        }
                                                    </label>
                                                    <div className="form-check form-switch mb-0 ms-3" style={{ pointerEvents: 'none' }}>
                                                        <input
                                                            type="checkbox"
                                                            className="form-check-input cursor-pointer"
                                                            checked={tempColumns.includes(col.key)}
                                                            readOnly
                                                        />
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                    <div className="card-footer bg-white border-top py-3 px-4 d-flex justify-content-end">
                                        <button
                                            onClick={handleSaveChangesColumnConfig}
                                            className="btn btn-primary btn-sm px-4 rounded-3 fw-semibold w-100"
                                        >
                                            Save Changes
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    {inlineButtons.length > 0 && (
                        <div className="d-flex align-items-center gap-2">
                            {inlineButtons.map((btn, idx) => (
                                <Fragment key={idx}>{btn}</Fragment>
                            ))}
                        </div>
                    )}
                    </div>
                    </div>

                {extraButtons.length > 0 && (
                    <div className="d-flex align-items-start gap-2 w-100">
                        {extraButtons.map((btn, idx) => (
                            <Fragment key={idx}>{btn}</Fragment>
                        ))}
                    </div>
                )}
            </div>
            )}

            {/* TABLE */}
            {tableConfig.loading ? (
                <SkeletonTableLoading columns={columns} rows={tableConfig.loadingRows || 10} />
            ) : (
            <Fragment>
            <div className={cx("tableWrap", "table-responsive", "global-hide-scrollbar")} ref={tableWrapRef} onScroll={handleTableScroll}>
                <table className={cx("dataTable", "table table-sm fs-9 mb-0")} ref={tableRef}>
                    <thead>
                    <tr>
                        {orderedColumns.map((col, index) => {
                            if (!visibleColumns.includes(col.key)) return null;
                            const isSortable = onSort && col.type !== "checkbox" && !(col.key && col.key.includes("Action")) && col.sortable !== false;
                            const isReorderable = columnOrder.includes(col.key);
                            return (
                                <th
                                    key={index}
                                    className={`align-middle ${col.headerClassName || ""} px-2 py-3 ${isSortable ? "cursor-pointer user-select-none" : ""} ${dragOverHeaderKey === col.key ? 'bg-primary-subtle border-start border-primary border-3' : ''} ${draggedHeaderKey === col.key ? 'opacity-50' : ''}`}
                                    style={{ minWidth: col.width || 'auto' }}
                                    onClick={() => isSortable && onSort(col.key)}
                                    draggable={isReorderable}
                                    onDragStart={(e) => isReorderable && handleDragStartHeader(e, col.key)}
                                    onDragOver={(e) => isReorderable && handleDragOverHeader(e, col.key)}
                                    onDrop={(e) => isReorderable && handleDropHeader(e, col.key)}
                                    onDragEnd={() => { setDraggedHeaderKey(null); setDragOverHeaderKey(null); }}
                                >
                                    <div className={`d-flex align-items-center ${(col.headerClassName || "").includes('text-center') ? 'justify-content-center' : ''}`}>
                                        {col.headerTemplate ? col.headerTemplate(data) : col.type === "checkbox" ? (
                                            <div className="form-check fs-8 mb-0">
                                                <input type="checkbox" className="form-check-input" />
                                            </div>
                                        ) : (
                                            <span className="me-2">{col.name || ""}</span>
                                        )}
                                        {isSortable && (
                                            <FontAwesomeIcon
                                                icon={sortColumn === col.key ? (sortDirection === "ASC" ? faSortUp : faSortDown) : faSort}
                                                className={sortColumn === col.key ? "text-primary" : "text-muted opacity-50"}
                                                style={{ fontSize: "0.85em" }}
                                            />
                                        )}
                                    </div>
                                </th>
                            );
                        })}
                    </tr>
                    </thead>

                    <tbody>
                    {data.length === 0 ? (
                        <tr>
                            <td
                                colSpan={visibleColumns.length}
                                className={cx("emptyState", "text-center py-4 fs-8-1 text-muted fw-semibold")}
                            >
                                No record found
                            </td>
                        </tr>
                    ) : groupedDataList ? (
                        groupedDataList.map((group, gIndex) => {
                            const isGroupExpanded = expandedRowGroups[group.key];
                            const firstRow = group.rows[0];
                            return (
                                <Fragment key={`group-${group.key}`}>
                                    {/* GROUP HEADER ROW */}
                                    <tr className="bg-light" style={{ borderBottom: '1px solid #dce0e5' }}>
                                        <td colSpan={visibleColumns.length} className="p-3">
                                            <div className="d-flex align-items-center">
                                                <div className="d-flex align-items-center cursor-pointer user-select-none" onClick={() => toggleRowGroup(group.key)}>
                                                    <div style={{ width: '24px', display: 'flex', justifyContent: 'center' }}>
                                                        <FontAwesomeIcon icon={isGroupExpanded ? faChevronDown : faChevronRight} className="text-secondary" />
                                                    </div>
                                                    <div className="ms-2">
                                                        {tableConfig.groupBy.template ? tableConfig.groupBy.template(firstRow) : firstRow[tableConfig.groupBy.key]}
                                                    </div>
                                                    <span className="badge bg-secondary mx-3 opacity-75">{group.rows.length} Plan(s)</span>
                                                </div>
                                                <div>
                                                    {tableConfig.groupBy.actionTemplate && tableConfig.groupBy.actionTemplate(firstRow)}
                                                </div>
                                            </div>
                                        </td>
                                    </tr>
                                    {/* GROUP CHILD ROWS */}
                                    {isGroupExpanded && group.rows.map((row, rIndex) => (
                                        <tr key={`child-${group.key}-${rIndex}`} className={cx("dataRow", { zebra: rIndex % 2 === 0 })}>
                                            {orderedColumns.map((col, cIndex) => {
                                                if (!visibleColumns.includes(col.key)) return null;

                                                const cellId = `${rIndex}-${col.key}`;
                                                const content = col.template ? col.template(row, rIndex, tableConfig.pageNumber, tableConfig.pageSize) : row[col.key];

                                                const spanCount = col.mergeBy ? spans[cellId] : 1;
                                                if (spanCount === 0) return null;

                                                const isExpanded = expandedCells[cellId];
                                                const shouldTruncate = col.isMoreLess && typeof content === "string" && content.length > 100;
                                                const displayContent = shouldTruncate && !isExpanded ? content.slice(0, 100) + "..." : content;

                                                return (
                                                    <td
                                                        key={cIndex}
                                                        rowSpan={spanCount > 1 ? spanCount : undefined}
                                                        className={`align-middle p-2 ${col.className || ""} ${spanCount > 1 ? "bg-white border-bottom" : ""}`}
                                                    >
                                                        {displayContent}
                                                        {shouldTruncate && (
                                                            <a href="#" className="ms-2 text-primary" onClick={(e) => { e.preventDefault(); toggleCell(rIndex, col.key); }}>
                                                                {isExpanded ? "less" : "more"}
                                                            </a>
                                                        )}
                                                    </td>
                                                );
                                            })}
                                        </tr>
                                    ))}
                                </Fragment>
                            );
                        })
                    ) : (
                        data.map((row, rIndex) => (
                            <tr key={rIndex} className={cx("dataRow", { zebra: rIndex % 2 === 0 })}>
                                {orderedColumns.map((col, cIndex) => {
                                    if (!visibleColumns.includes(col.key)) return null;

                                    const cellId = `${rIndex}-${col.key}`;
                                    const content = col.template
                                        ? col.template(row, rIndex, tableConfig.pageNumber, tableConfig.pageSize)
                                        : row[col.key];

                                    const spanCount = col.mergeBy ? spans[cellId] : 1;
                                    if (spanCount === 0) return null;

                                    const isExpanded = expandedCells[cellId];
                                    const shouldTruncate =
                                        col.isMoreLess &&
                                        typeof content === "string" &&
                                        content.length > 100;

                                    const displayContent =
                                        shouldTruncate && !isExpanded
                                            ? content.slice(0, 100) + "..."
                                            : content;

                                    return (
                                        <td
                                            key={cIndex}
                                            rowSpan={spanCount > 1 ? spanCount : undefined}
                                            className={`align-middle p-2 ${col.className || ""} ${spanCount > 1 ? "bg-white border-bottom" : ""}`}
                                        >
                                            {displayContent}
                                            {shouldTruncate && (
                                                <a
                                                    href="#"
                                                    className="ms-2 text-primary"
                                                    onClick={(e) => {
                                                        e.preventDefault();
                                                        toggleCell(rIndex, col.key);
                                                    }}
                                                >
                                                    {isExpanded ? "less" : "more"}
                                                </a>
                                            )}
                                        </td>
                                    );
                                })}
                            </tr>
                        ))
                    )}
                    </tbody>
                </table>
            </div>

            {/* FAKE STICKY HORIZONTAL SCROLLBAR */}
            {tableWidth > 0 && (
                <div 
                    ref={customScrollbarRef}
                    onScroll={handleCustomScrollbarScroll}
                    className="custom-sticky-scrollbar"
                    style={{ 
                        position: 'sticky', 
                        bottom: 0, 
                        overflowX: 'auto', 
                        overflowY: 'hidden', 
                        zIndex: 10,
                        backgroundColor: 'rgba(255, 255, 255, 0.95)',
                        backdropFilter: 'blur(4px)',
                        borderTop: '1px solid #e2e8f0',
                        borderBottomRightRadius: '14px',
                        borderBottomLeftRadius: '14px'
                    }}
                >
                    <style>{`
                        .custom-sticky-scrollbar::-webkit-scrollbar {
                            height: 12px !important;
                            background-color: #f1f1f1 !important;
                            border-radius: 0 0 14px 14px;
                        }
                        .custom-sticky-scrollbar::-webkit-scrollbar-thumb {
                            background-color: #888888 !important;
                            border-radius: 10px;
                            border: 2px solid #f1f1f1;
                        }
                        .custom-sticky-scrollbar::-webkit-scrollbar-thumb:hover {
                            background-color: #555555 !important;
                        }
                        /* Ensure the actual table scrollbar is hidden completely */
                        .global-hide-scrollbar::-webkit-scrollbar {
                            display: none !important;
                        }
                        .global-hide-scrollbar {
                            scrollbar-width: none !important;
                            -ms-overflow-style: none !important;
                        }
                    `}</style>
                    <div style={{ width: tableWidth, height: '1px' }}></div>
                </div>
            )}
            </Fragment>
            )}
        </div>
    );
}

export default Table;
